import React, { createContext, useCallback, useState, useContext } from 'react'
import jwtDecode, { JwtPayload } from 'jwt-decode'

import api from '../services/api'

interface User {
  id: string
  name: string
  username: string
  password: string
  avatar_url: string
  role_id: number
}

interface Menu {
  id: number
  parent_id?: number
  method?: string
  name: string
  url?: string
  permission: boolean
  children?: Menu[]
  type: string
}

interface AuthState {
  token: string
  user: User
  menus: Menu[]
}

interface SingInCredentials {
  username: string
  password: string
}

interface AuthContextData {
  user: User
  menus: Menu[]
  backupHistory: string
  signIn(credentials: SingInCredentials): Promise<void>
  signOut(): void
  userLogged(): boolean
  updateuser(): void
  setHistory(string: string): void
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData)

export const AuthProvider: React.FC = ({ children }) => {
  const [backupHistory, setBackupHistory] = useState(() => {
    const backupHistory = localStorage.getItem('@B2BMicroCredit:BackupHistory')
    if (backupHistory) {
      return backupHistory
    }
    return ''
  })
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@B2BMicroCredit:token')
    const user = localStorage.getItem('@B2BMicroCredit:user')
    const menus = localStorage.getItem('@B2BMicroCredit:menus')

    if (token && user && menus) {
      api.defaults.headers.authorization = `Bearer ${token}`
      return { token, user: JSON.parse(user), menus: JSON.parse(menus) }
    }
    return {} as AuthState
  })

  const signIn = useCallback(async ({ username, password }) => {
    const response = await api.post('/sessions', {
      username,
      password
    })

    const { token, user, menus } = response.data

    localStorage.setItem('@B2BMicroCredit:token', token)
    localStorage.setItem('@B2BMicroCredit:user', JSON.stringify(user))
    localStorage.setItem('@B2BMicroCredit:menus', JSON.stringify(menus))

    api.defaults.headers.authorization = `Bearer ${token}`

    setData({ token, user, menus })
  }, [])

  const setHistory = useCallback((history: string) => {
    localStorage.setItem('@B2BMicroCredit:BackupHistory', history)
    setBackupHistory(history)
  }, [])
  const userLogged = useCallback(() => {
    const token = localStorage.getItem('@B2BMicroCredit:token')
    if (token) {
      const decode = jwtDecode<JwtPayload>(token)
      const currentDate = Date.now().valueOf() / 1000
      if (decode.exp < currentDate) {
        return false
      } else {
        return true
      }
    }
    return false
  }, [])
  const updateuser = useCallback(async () => {
    const user = await (await api.get('/profile/show')).data

    setData({ token: data.token, user, menus: data.menus })

    localStorage.setItem('@B2BMicroCredit:user', JSON.stringify(user))
  }, [data.menus, data.token])

  const signOut = useCallback(() => {
    localStorage.removeItem('@B2BMicroCredit:token')
    localStorage.removeItem('@B2BMicroCredit:user')
    localStorage.removeItem('@B2BMicroCredit:menus')

    setData({} as AuthState)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        setHistory,
        backupHistory,
        user: data.user,
        menus: data.menus,
        signIn,
        signOut,
        userLogged,
        updateuser
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AutProvider')
  }

  return context
}
